import { Toaster as Sonner } from "@/components/ui/sonner";
import { RouteFallback } from "@/components/ui/route-fallback";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";

// Eagerly loaded: landing + auth entry pages (critical for first paint / common entry)
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import { PricingRouteGate } from "@/components/billing/PricingRouteGate";
import { TierGate } from "@/components/billing/TierGate";
import { Palette, BarChart3, CalendarClock, Users } from "lucide-react";

// Executive layout shells stay eager (small)
import ExecutiveLayout from "@/components/layout/ExecutiveLayout";
import RoleProtectedRoute from "@/components/auth/RoleProtectedRoute";
import { AdminGate, BillingRouteGate } from "@/components/admin/AdminGate";

// Lazy-loaded routes — code-split to reduce initial JS bundle
const Signup = lazy(() => import("./pages/Signup"));
const SignIn = lazy(() => import("./pages/SignIn"));
const ContributorSignIn = lazy(() => import("./pages/ContributorSignIn"));
const Submit = lazy(() => import("./pages/Submit"));
const MyAchievements = lazy(() => import("./pages/MyAchievements"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const BillingReturn = lazy(() => import("./pages/BillingReturn"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Home = lazy(() => import("./pages/Home"));
const Templates = lazy(() => import("./pages/Templates"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));
const Cycles = lazy(() => import("./pages/Cycles"));
const CycleDetail = lazy(() => import("./pages/CycleDetail"));
const Report = lazy(() => import("./pages/Report"));
const ReportEditor = lazy(() => import("./pages/ReportEditor"));
const ReportsArchive = lazy(() => import("./pages/ReportsArchive"));
const Analytics = lazy(() => import("./pages/Analytics"));
const BrandSettings = lazy(() => import("./pages/BrandSettings"));
const Schedules = lazy(() => import("./pages/Schedules"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const AdminContributors = lazy(() => import("./pages/AdminContributors"));
const ValueCalculator = lazy(() => import("./pages/ValueCalculator"));
const AdminMagicLinks = lazy(() => import("./pages/AdminMagicLinks"));
const Reliability = lazy(() => import("./pages/admin/Reliability"));
const AdminConsole = lazy(() => import("./pages/admin/AdminConsole"));
const AdminWorkspace = lazy(() => import("./pages/admin/AdminWorkspace"));
const OrgSettings = lazy(() => import("./pages/OrgSettings"));
const Billing = lazy(() => import("./pages/Billing"));
const TeamMembers = lazy(() => import("./pages/TeamMembers"));
const Executives = lazy(() => import("./pages/Executives"));
const ExecutiveDashboard = lazy(() => import("./pages/executive/ExecutiveDashboard"));
const RollupsList = lazy(() => import("./pages/executive/RollupsList"));
const NewRollupWizard = lazy(() => import("./pages/executive/NewRollupWizard"));
const RollupView = lazy(() => import("./pages/executive/RollupView"));
const SharedReportView = lazy(() => import("./pages/executive/SharedReportView"));
const TemplateActivity = lazy(() => import("./pages/TemplateActivity"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/contributor" element={<ContributorSignIn />} />
            <Route path="/submit/:token" element={<Submit />} />
            <Route path="/my-achievements" element={<MyAchievements />} />
            <Route path="/pricing" element={<PricingRouteGate />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/billing/return" element={<BillingRouteGate><BillingReturn /></BillingRouteGate>} />

            {/* Hidden platform admin route — guarded inside the component */}
            <Route
              path="/admin"
              element={
                <AdminGate>
                  <AdminConsole />
                </AdminGate>
              }
            />
            <Route
              path="/admin/workspaces/:orgId"
              element={
                <AdminGate>
                  <AdminWorkspace />
                </AdminGate>
              }
            />
            <Route
              path="/admin/magic-links"
              element={
                <ProtectedRoute>
                  <AdminMagicLinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reliability"
              element={
                <ProtectedRoute>
                  <Reliability />
                </ProtectedRoute>
              }
            />

            {/* Protected routes with sidebar layout */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/home" element={<Home />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/templates/:id" element={<TemplateDetail />} />
              <Route path="/templates/:id/activity" element={<TemplateActivity />} />
              <Route path="/rollups/:id" element={<RollupView />} />
              <Route path="/cycles" element={<Cycles />} />
              <Route path="/cycles/:id" element={<CycleDetail />} />
              <Route path="/reports/:cycleId" element={<Report />} />
              <Route path="/reports/:cycleId/edit" element={<ReportEditor />} />
              <Route path="/archive" element={<ReportsArchive />} />
              <Route path="/analytics" element={
                <TierGate
                  requiredTier="team"
                  feature="Analytics dashboard"
                  icon={BarChart3}
                  description="See trends, contributor performance, and section-level metrics across every cycle. Spot what's improving — and where things are slipping — at a glance."
                  benefits={[
                    "Organization-wide metric trends and percent change",
                    "Contributor leaderboards and section heatmaps",
                    "Time-series charts for every tracked metric",
                  ]}
                >
                  <Analytics />
                </TierGate>
              } />
              <Route path="/schedules" element={
                <TierGate
                  requiredTier="team"
                  feature="Recurring schedules"
                  icon={CalendarClock}
                  description="Automate your reporting cadence. Schedules create cycles, send invites, and follow up on deadlines automatically — so you never have to chase contributors down."
                  benefits={[
                    "Weekly, biweekly, monthly, or quarterly cadences",
                    "Smart business-day scheduling (no weekend deadlines)",
                    "Automatic reminder emails and overdue alerts",
                  ]}
                >
                  <Schedules />
                </TierGate>
              } />
              <Route path="/settings/brand" element={
                <TierGate
                  requiredTier="team"
                  feature="Custom branding"
                  icon={Palette}
                  description="Make every report look like it came from your organization — not a generic template. Upload your logo, set brand colors, and pick fonts that match your style guide."
                  benefits={[
                    "Logo upload with positioning controls",
                    "Custom primary, secondary, and card color palettes",
                    "Curated font pairings and layout presets",
                  ]}
                >
                  <BrandSettings />
                </TierGate>
              } />
              <Route path="/settings/notifications" element={<NotificationSettings />} />
              <Route path="/settings/organization" element={<OrgSettings />} />
              <Route path="/settings/team" element={
                <TierGate
                  requiredTier="organization"
                  feature="Team members"
                  icon={Users}
                  description="Invite teammates as managers so they can run cycles, edit reports, and collaborate on briefings — all under your organization's plan."
                  benefits={[
                    "Up to 5 manager seats included on Organization",
                    "Member or Admin roles per teammate",
                    "Email-based invites with one-click accept on sign-in",
                  ]}
                >
                  <TeamMembers />
                </TierGate>
              } />
              <Route path="/admin/contributors" element={<AdminContributors />} />
              <Route path="/executives" element={<Executives />} />
              <Route path="/value" element={<ValueCalculator />} />
              <Route path="/settings/billing" element={<BillingRouteGate><Billing /></BillingRouteGate>} />
            </Route>

            {/* Executive routes */}
            <Route
              element={
                <RoleProtectedRoute requireRole="executive">
                  <ExecutiveLayout />
                </RoleProtectedRoute>
              }
            >
              <Route path="/executive" element={<ExecutiveDashboard />} />
              <Route path="/executive/rollups" element={<RollupsList />} />
              <Route path="/executive/rollups/new" element={<NewRollupWizard />} />
              <Route path="/executive/rollups/:id" element={<RollupView />} />
              <Route path="/executive/templates/:id/activity" element={<TemplateActivity />} />
              <Route path="/executive/shared/:reportId" element={<SharedReportView />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
